import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  isNotificationSoundEnabled,
  persistNotificationSoundEnabled,
  playNotificationChime,
  unlockNotificationAudio,
} from "../utils/notificationSound";
import {
  isDesktopNotificationEnabled,
  setDesktopNotificationPreference,
} from "../utils/notificationDesktop";
import { showDesktopNotification } from "../utils/notificationDisplay";

const AppContext = createContext(null);

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const SESSION_CHECK_MS = 60_000;

function parseJwtPayload(token) {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function sessionExpiresAtFromToken(token) {
  const payload = parseJwtPayload(token);
  if (payload?.sess_exp) {
    return new Date(payload.sess_exp * 1000).toISOString();
  }
  return null;
}

function redirectToLogin(expired = false) {
  TokenStorage.clear();
  const qs = expired ? "?expired=1" : "";
  if (window.location.pathname !== "/login") {
    window.location.href = `/login${qs}`;
  }
}

// ─── Token helpers ───────────────────────────────────────────────────────────
const TokenStorage = {
  save: (accessToken, refreshToken, sessionExpiresAt) => {
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    const expiresAt =
      sessionExpiresAt || sessionExpiresAtFromToken(accessToken);
    if (expiresAt) {
      localStorage.setItem("session_expires_at", expiresAt);
    }
  },
  getAccess: () => localStorage.getItem("access_token"),
  getRefresh: () => localStorage.getItem("refresh_token"),
  getSessionExpiresAt: () => localStorage.getItem("session_expires_at"),
  isSessionExpired: () => {
    const exp = localStorage.getItem("session_expires_at");
    if (!exp) return false;
    return Date.now() >= new Date(exp).getTime();
  },
  clear: () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("session_expires_at");
    localStorage.removeItem("current_user");
  },
  saveUser: (u) => localStorage.setItem("current_user", JSON.stringify(u)),
  loadUser: () => {
    try {
      if (TokenStorage.isSessionExpired()) return null;
      return JSON.parse(localStorage.getItem("current_user"));
    } catch {
      return null;
    }
  },
};

// ─── Authenticated fetch ─────────────────────────────────────────────────────
export async function apiFetch(path, options = {}) {
  if (TokenStorage.isSessionExpired()) {
    redirectToLogin(true);
    throw new Error("Session expired");
  }

  const token = TokenStorage.getAccess();
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const retryHeaders = {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
        Authorization: `Bearer ${TokenStorage.getAccess()}`,
      };
      const retryRes = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: retryHeaders,
      });
      if (!retryRes.ok) {
        console.error(
          `[apiFetch] retry after refresh failed: ${path} → ${retryRes.status}`,
        );
      }
      return retryRes;
    }
    console.warn(
      `[apiFetch] token refresh failed, redirecting to login (was calling ${path})`,
    );
    redirectToLogin(true);
    throw new Error("Session expired");
  }
  return res;
}

function resolveAttachmentRequest(apiPath, inlineOption = true) {
  const qIndex = apiPath.indexOf("?");
  const basePath = qIndex >= 0 ? apiPath.slice(0, qIndex) : apiPath;
  const params = new URLSearchParams(qIndex >= 0 ? apiPath.slice(qIndex + 1) : "");
  const inlineParam = params.get("inline");
  let inlinePreview = inlineOption;
  if (inlineParam != null) {
    inlinePreview = inlineParam === "true" || inlineParam === "1";
  }
  params.set("inline", inlinePreview ? "true" : "false");
  const qs = params.toString();
  return {
    fetchPath: qs ? `${basePath}?${qs}` : basePath,
    inlinePreview,
  };
}

function filenameFromResponse(res, fallback = "download") {
  const cd = res.headers.get("content-disposition") || "";
  const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)/i);
  if (!match) return fallback;
  const raw = match[1].replace(/"/g, "").trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Open an authenticated attachment in a new browser tab (inline preview). */
export async function openAttachmentPreview(apiPath, { inline = true } = {}) {
  const { fetchPath, inlinePreview } = resolveAttachmentRequest(apiPath, inline);

  // Open the tab synchronously (during the click) so pop-up blockers allow it.
  let previewTab = null;
  if (inlinePreview) {
    previewTab = window.open("about:blank", "_blank");
    if (!previewTab) {
      throw new Error("Pop-up blocked — allow pop-ups to preview this file");
    }
    previewTab.opener = null;
    previewTab.document.title = "Loading preview…";
    previewTab.document.body.innerHTML =
      '<p style="font-family:sans-serif;padding:24px;color:#475569;">Loading preview…</p>';
  }

  try {
    const res = await apiFetch(fetchPath);
    if (!res.ok) {
      let detail = "Could not open attachment";
      try {
        const data = await res.json();
        detail = data.detail || detail;
      } catch {
        /* ignore */
      }
      throw new Error(
        typeof detail === "string" ? detail : "Could not open attachment",
      );
    }

    const blob = await res.blob();

    if (inlinePreview) {
      const url = URL.createObjectURL(blob);
      previewTab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 120000);
      return;
    }

    downloadBlob(blob, filenameFromResponse(res));
  } catch (err) {
    if (previewTab && !previewTab.closed) previewTab.close();
    throw err;
  }
}

/** @deprecated alias — use openAttachmentPreview */
export const openAuthenticatedAttachment = openAttachmentPreview;

async function tryRefreshToken() {
  if (TokenStorage.isSessionExpired()) return false;
  const rt = TokenStorage.getRefresh();
  if (!rt) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    TokenStorage.save(
      data.access_token,
      data.refresh_token,
      data.session_expires_at,
    );
    return true;
  } catch {
    return false;
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    if (TokenStorage.isSessionExpired()) {
      TokenStorage.clear();
      return null;
    }
    return TokenStorage.loadUser();
  });
  const [notification, setNotification] = useState(null);
  const [notificationSoundEnabled, setNotificationSoundEnabledState] = useState(
    isNotificationSoundEnabled,
  );
  const [desktopNotificationEnabled, setDesktopNotificationEnabledState] =
    useState(isDesktopNotificationEnabled);

  useEffect(() => {
    if (!currentUser) return undefined;
    if (TokenStorage.isSessionExpired()) {
      setCurrentUser(null);
      redirectToLogin(true);
      return undefined;
    }
    const timer = setInterval(() => {
      if (TokenStorage.isSessionExpired()) {
        setCurrentUser(null);
        redirectToLogin(true);
      }
    }, SESSION_CHECK_MS);
    return () => clearInterval(timer);
  }, [currentUser]);

  useEffect(() => {
    const existing = TokenStorage.loadUser();
    if (!existing?.role || existing.role === "admin") return undefined;
    const path =
      existing.role === "tenant_admin"
        ? "/auth/me/tenant-admin"
        : "/auth/me/user";
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(path);
        if (!res.ok || cancelled) return;
        const profile = await res.json();
        setCurrentUser((prev) => {
          if (!prev) return prev;
          const next = {
            ...prev,
            ...profile,
            role: prev.role,
            user_id: prev.user_id,
            tenant_slug: prev.tenant_slug,
            access_token: prev.access_token,
            session_expires_at: prev.session_expires_at,
          };
          TokenStorage.saveUser(next);
          return next;
        });
      } catch {
        /* keep cached session */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unlock = () => unlockNotificationAudio();
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const setNotificationSoundEnabled = useCallback((enabled) => {
    const next = Boolean(enabled);
    persistNotificationSoundEnabled(next);
    setNotificationSoundEnabledState(next);
    if (next) {
      unlockNotificationAudio();
      playNotificationChime({ force: true });
    }
  }, []);

  const playNotificationSound = useCallback(() => {
    playNotificationChime();
  }, []);

  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  }, []);

  const setDesktopNotificationEnabled = useCallback(
    async (enabled) => {
      const result = await setDesktopNotificationPreference(Boolean(enabled));
      setDesktopNotificationEnabledState(result.enabled);
      if (!enabled) {
        showNotification("Desktop notifications turned off", "success");
        return result;
      }
      if (result.ok) {
        showNotification("Desktop notifications enabled", "success");
        showDesktopNotification(
          "Desktop notifications on",
          "You will get alerts for new inbox items while this tab is in the background.",
          { silent: !isNotificationSoundEnabled() },
        );
        return result;
      }
      if (result.reason === "denied" || result.reason === "blocked") {
        showNotification(
          "Browser blocked desktop notifications. Allow them in site settings, then try again.",
          "error",
        );
      } else if (result.reason === "unsupported") {
        showNotification(
          "Desktop notifications are not supported in this browser.",
          "error",
        );
      }
      return result;
    },
    [showNotification],
  );

  // ── LOOKUP: given email → returns { found, tenant_slug, company_name, role, display_name }
  // Calls POST /auth/lookup (no password, public endpoint)
  const lookupUser = useCallback(async (email) => {
    try {
      const res = await fetch(`${API_BASE}/auth/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      return await res.json();
    } catch {
      return { found: false, error: "Network error. Is the backend running?" };
    }
  }, []);

  // ── LOGIN: email + password + tenant_slug (auto-filled from lookupUser)
  const login = useCallback(async (email, password, tenantSlug = "") => {
    try {
      const body = { email: email.trim(), password };
      if (tenantSlug) body.tenant_slug = tenantSlug;

      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok)
        return { success: false, error: data.detail || "Invalid credentials" };

      TokenStorage.save(
        data.access_token,
        data.refresh_token,
        data.session_expires_at,
      );

      // Fetch profile
      const profilePath =
        data.role === "admin"
          ? "/auth/me/admin"
          : data.role === "tenant_admin"
            ? "/auth/me/tenant-admin"
            : "/auth/me/user";

      const profileRes = await fetch(`${API_BASE}${profilePath}`, {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      let profile = {};
      if (profileRes.ok) profile = await profileRes.json();

      const user = {
        ...profile,
        role: data.role,
        user_id: data.user_id,
        tenant_slug: data.tenant_slug || null,
        access_token: data.access_token,
        session_expires_at: data.session_expires_at || null,
      };

      TokenStorage.saveUser(user);
      setCurrentUser(user);
      return { success: true, role: data.role };
    } catch {
      return {
        success: false,
        error: "Network error. Is the backend running?",
      };
    }
  }, []);

  // ── LOGOUT
  const logout = useCallback(async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {}
    TokenStorage.clear();
    setCurrentUser(null);
  }, []);

  return (
    <AppContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        login,
        logout,
        lookupUser,
        notification,
        showNotification,
        notificationSoundEnabled,
        setNotificationSoundEnabled,
        playNotificationSound,
        desktopNotificationEnabled,
        setDesktopNotificationEnabled,
        apiFetch,
        openAttachmentPreview,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
